import './BetbookPostItem.less';

import clsx from 'clsx';
import React, { useContext, useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import i18n from '@i18n';
import PopupNg from '@/ui/PopupNg';
import { fireGAEvent } from '@/analytics';
import Button from '@/common/component/Button';
import { addAnalysisToChat } from '@/betbook/handlers';
import { getConfigProperty } from '@/common/oldMobile';
import { nike1Bet$, nike1SportEvent$ } from '@/nikeone/subs';
import BetbookPostTicket from '@/betbook/views/BetbookPostTicket';
import TicketDetailLoader from '@/mytickets/detail/views/TicketDetailLoader';
import BetbookChart from 'nike-ui/src/components/betbook-chart/BetbookChart';
import { accountUserData$, loggedIn$ } from '@/account/subs';
import { marketStatsBetbookBets$ } from 'nike-ui/src/components/market-statistics/reframe/h&s';
import { ticketDetail$, ticketMinimal$ } from '@/mytickets/detail/subs';
import BetLocationContext, { betLocation, getModalLocation } from 'nike-ui/src/common/BetLocationContext';
import LogOutIcon from '@/icons/LogOutIcon';
import { betbookDetailRoute } from '@/betbook/utils';
import { createNikeBetbookPostShareURL } from 'nike-ui/src/common/util';
import isEditable from 'nike-ui/src/components/betbook/utils/isEditable';
import { POST_ANALYSIS, postTypeEnum2Url } from 'nike-ui/src/components/betbook/constants';
import useBoxStore from '@/nikeone/hook/useBoxStore';
import { BETBOOK } from '@/nikeone/context/BoxOptionsContext';
import { SvgIcon } from 'nike-ui/src/icons/SvgIcon';
import BetbookAnalysisShareIcon from '@/icons/BetbookAnalysisShareIcon';
import RouteLink from 'nike-ui/src/common/routing/RouteLink';
import BetbookAnalysisOverview from 'nike-ui/src/components/betbook-analysis-overview/BetbookAnalysisOverview';
import BetbookAnalysisDetail from 'nike-ui/src/components/betbook-analysis-detail/BetbookAnalysisDetail';
import NuiBet from 'nike-ui/src/components/nui-bet/NuiBet';
import moment from 'moment';
import { MarketStats } from 'nike-ui/src/components/market-statistics/dto';

const BOXSTORE = 'betbook';

export function BetbookAnalysisItem({ post, detail = false }) {
    const { subscribe } = useReframe();
    const { id: postId, title, urlSuffix, creationDate, editableUntil } = post || {};
    const { author, pick, reward, ticket: postTicket } = post || {};
    const { betId, betSelectionCode, odds: initialOdds, matchId, market, expiration } = pick || {};
    const { statusReason } = reward || {};
    const { hash } = postTicket || {};
    const sportId = post?.pick?.sportId;

    useBoxStore(BOXSTORE, BETBOOK);
    const betLocationContext = useContext(BetLocationContext);
    const loggedIn = subscribe(loggedIn$);
    const { nickname: currentUserNickname } = subscribe(accountUserData$);
    const userPost = author?.nickname === currentUserNickname;
    const bet = subscribe(nike1Bet$(BOXSTORE, betId));
    const sportEvent = subscribe(nike1SportEvent$(BOXSTORE, bet?.sportEventId));
    const ticket = subscribe(ticketDetail$(hash)) || subscribe(ticketMinimal$(hash));
    const marketStatsBetbookBets = subscribe(marketStatsBetbookBets$(matchId));
    const marketStatisticBet = marketStatsBetbookBets?.find(bet => bet.betId === betId);
    const filteredSelection = marketStatisticBet?.selections?.find(
        (sel: MarketStats.BetSelectionDto) => sel.selection === betSelectionCode
    );
    const editorTipCode = pick?.selectionGrid.flat().find(selection => selection?.editorTip === true)?.tip;
    const stakeShare = filteredSelection?.stakeShareGrouped || filteredSelection?.stakeShare;
    const editable = userPost && isEditable(POST_ANALYSIS, creationDate, pick?.expiration, editableUntil);
    const isWeekAfterExpiration = moment(expiration)
        .add(7, 'days')
        .isBefore(moment());

    function openBetbookStatisticsModal(e) {
        e.stopPropagation();
        e.preventDefault();
        //@ts-ignore
        const sectionOrigin = getModalLocation(betLocationContext?.betLocationSection.id);
        PopupNg.dispatcher.open('betbook/statisticsDialog', { matchId, sportId, sportEvent, market, sectionOrigin }, 2);
    }

    function openOptionsDialog(e) {
        e.stopPropagation();
        e.preventDefault();
        PopupNg.dispatcher.open('betbook/postOptions', { postType: POST_ANALYSIS, postId, editable });
    }

    function openProfileDialog(e) {
        e.stopPropagation();
        e.preventDefault();
        const avatarId = post?.author?.avatarId;
        const nickname = post?.author?.nickname;

        fireGAEvent('Betbook Profile Dialog', 'Open');
        PopupNg.dispatcher.open('betbook/profileDialog', { avatarId, nickname });
    }

    function openRewardInfoFail(e) {
        e.stopPropagation();
        e.preventDefault();
        const title = '';
        const text = `bb.rewards.${statusReason}`;
        PopupNg.dispatcher.open('betbook/infoDialog', { title, text });
    }

    const betBox = (overview, detail) =>
        betId && pick ? (
            <>
                <NuiBet
                    betData={isWeekAfterExpiration ? pick : bet}
                    isLoading={!bet}
                    prevOdds={initialOdds}
                    betLocationSection={
                        detail ? betLocation.COMMUNITY_ANALYSIS_DETAIL : betLocation.BETBOOK_OVERVIEW_ANALYSIS
                    }
                    selectionCode={detail ? null : betSelectionCode}
                    editorTipCode={editorTipCode}
                />
                {!overview && stakeShare && (
                    <BetbookChart stakeShare={stakeShare} onClick={openBetbookStatisticsModal} overview={!detail} />
                )}
            </>
        ) : null;

    const ticketBox = hash ? <BetbookPostItemTicket ticket={ticket} detail={true} hash={hash} /> : null;

    const rewardInfoButton = (
        <button onClick={openRewardInfoFail}>
            <SvgIcon imgId="info" className="c-black-40 info-icon" sizeX="16px" sizeY="16px" />
        </button>
    );
    return (
        <>
            {detail ? (
                <BetbookAnalysisDetail
                    betComponent={betBox(false, true)}
                    betbookPostItemTicket={ticketBox}
                    betbookDetailReactions={<BetbookDetailReactions postId={postId} postTitle={title} />}
                    betbookRewardProgressBarChildren={rewardInfoButton}
                    isLoggedIn={loggedIn}
                    post={post}
                    onProfileDialog={openProfileDialog}
                    onOptionDialog={openOptionsDialog}
                    ownPost={userPost}
                />
            ) : (
                <RouteLink
                    route={betbookDetailRoute(POST_ANALYSIS)}
                    params={{ postId, postUrlSuffix: urlSuffix || '_' }}
                    className="c-black"
                >
                    <BetbookAnalysisOverview
                        betComponent={betBox(true, false)}
                        betbookRewardProgressBarChildren={rewardInfoButton}
                        openProfileDialog={openProfileDialog}
                        post={post}
                        ownPost={userPost}
                        isPhone
                    />
                </RouteLink>
            )}
        </>
    );
}

function BetbookPostItemTicket({ ticket, detail, hash }) {
    if (!ticket && detail) {
        return <TicketDetailLoader />;
    }
    if (!ticket || ticket.error) {
        return null;
    }
    if (detail) {
        return (
            <BetbookPostTicket
                ticket={ticket}
                isAnalysis={true}
                hash={hash}
                label={detail ? i18n('betbook.ticket.label') : null}
            />
        );
    }
    return null;
}

function BetbookDetailReactions({ postId, postTitle }) {
    return (
        <div className="betbook-detail-reactions">
            <ShareAnalysisButton analysisId={postId} />
            <SharedButton postId={postId} postTitle={postTitle} />
        </div>
    );
}

function SharedButton({ postTitle, postId }) {
    const [active, setActive] = useState(false);

    function onClick(e) {
        if (!active) {
            sharePost(e, postId, postTitle);
        }
        setActive(!active);
    }

    return (
        <div className={clsx('betbook-reaction-btn', { active })}>
            <Button
                data-atid="betbook-share-btn-facebook"
                className="betbook-reactions-option flex items-center fs-12 px-5 py-5 text-bold"
                onClick={onClick}
            >
                <LogOutIcon className="py-5 px-5" />
            </Button>
            <span className="betbook-reaction-text">
                {active ? i18n('common.shared.link') : i18n('common.share.link')}
            </span>
        </div>
    );
}

function ShareAnalysisButton({ analysisId }) {
    return (
        <div className="betbook-reaction-btn">
            <button
                data-atid="betbook-share-btn-analysis"
                className="betbook-reactions-option flex items-center fs-12 px-5 py-5 text-bold"
                onClick={() => dispatch(addAnalysisToChat(analysisId))}
            >
                <BetbookAnalysisShareIcon className="py-5 px-3" />
            </button>
            <span data-atid="bb-share-chat" className="betbook-reaction-btn">
                {i18n('markdown.analysis.share.btn')}
            </span>
        </div>
    );
}

function sharePost(e, postId, postTitle) {
    e.preventDefault();
    e.stopPropagation();
    const baseUrl = getConfigProperty('hostUrl');
    const postType = POST_ANALYSIS;

    if (navigator.share) {
        const navigate = createNikeBetbookPostShareURL(
            `${baseUrl}/betbook/${postTypeEnum2Url[postType]}/${postId}/${postTitle}`,
            postId,
            postType,
            postTitle,
            'navigator'
        );

        navigator.share({ url: navigate });
    } else {
        const appId = getConfigProperty('facebook.key');
        // natvrdo produkcna scrap url, nema vyznam tu davat testovacie hosty, ked sa facebook z vonka na ne aj tak nedostane
        const hashtag = encodeURIComponent('#NikeFandisNaplno');

        const href = createNikeBetbookPostShareURL(
            `https://www.nike.sk/betbook/${postTypeEnum2Url[postType]}/${postId}/${postTitle}`,
            postId,
            postType,
            postTitle,
            'facebook'
        );

        const url = `https://www.facebook.com/dialog/share?app_id=${appId}&display=touch&href=${encodeURIComponent(
            href
        )}&hashtag=${hashtag}`;
        window.open(url, '_blank');
    }
}

export default BetbookAnalysisItem;
